import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { SEO_TOPICS } from "../data/siteContent";
import { SEO_T } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, articleSchema, webPageSchema, breadcrumbSchema } from "../seo/schemas";

export default function SEOTopic() {
  const { slug } = useParams();
  const topic = SEO_TOPICS[slug];

  if (!topic) {
    return (
      <SiteLayout>
        <PageHeader overline="404" title="Topic" italic="not found." kicker="The page you're looking for doesn't exist. Head back to the home page." />
        <div className="bg-white py-20 px-6 md:px-12">
          <Link to="/" className="btn-primary">Back Home <ArrowRight size={16} /></Link>
        </div>
      </SiteLayout>
    );
  }

  const allTopics = Object.entries(SEO_TOPICS).filter(([s]) => s !== slug).slice(0, 6);

  return (
    <SiteLayout>
      <Seo
        title={`${topic.h1} — Manuel Santana`}
        description={`${topic.h1} by Manuel Santana. ${topic.tagline} ${topic.body[0].slice(0, 140)}`}
        path={`/topics/${slug}`}
        type="article"
        schemas={buildSchemas([
          articleSchema({
            path: `/topics/${slug}`,
            headline: `${topic.h1} — Manuel Santana`,
            description: topic.tagline,
            datePublished: "2025-01-15",
          }),
          webPageSchema({ path: `/topics/${slug}`, name: topic.h1, description: topic.tagline }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Topics", path: "/topics/relationship-commentary" },
            { name: topic.h1, path: `/topics/${slug}` },
          ]),
        ])}
      />
      <PageHeader overline={`Topic · ${slug.replace(/-/g, " ")}`} title={topic.h1} italic={topic.tagline} />

      <section data-testid={SEO_T.page} className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[900px] px-6 md:px-12 space-y-8 text-black/75 text-lg md:text-xl leading-relaxed">
          {topic.body.map((p, i) => (<p key={i}>{p}</p>))}

          <div className="pt-10 flex flex-wrap gap-4">
            <Link to="/podcast" className="btn-primary">Listen To The Podcast <ArrowRight size={16} /></Link>
            <Link to="/books" className="btn-ghost">Read The Book</Link>
            <Link to="/contact" className="btn-ghost">Book An Interview</Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28 border-t border-black/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Related Topics</p>
          <div className="mt-10 grid grid-cols-12 gap-6">
            {allTopics.map(([s, t]) => (
              <Link key={s} to={`/topics/${s}`} className="col-span-12 md:col-span-6 lg:col-span-4 border border-black/15 hover:border-[#0a0a0a] p-6 transition-all">
                <h3 className="font-display text-xl text-[#0a0a0a]">{t.h1}</h3>
                <p className="mt-2 text-sm text-black/55">{t.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
